import { useEffect, useState } from 'react';
import { Pressable, View } from 'react-native';
import { useStyles } from 'react-native-unistyles';

import { Text } from '@/ui';
import { Icon } from '@/ui/icon';

import { ChevronDown } from '../icons/chevron-down';
import { ChevronUp } from '../icons/chevron-up';

interface CollapsibleProps {
  title: string;
  children: React.ReactNode;
  collapsed?: boolean;
  onToggle?: (collapsed: boolean) => void;
  headerRight?: React.ReactNode;
  groupPosition?: 'top' | 'bottom';
}

export default function Collapsible({
  title,
  children,
  collapsed = false,
  onToggle,
  headerRight,
  groupPosition,
}: CollapsibleProps) {
  const { theme } = useStyles();
  const [isCollapsed, setIsCollapsed] = useState(collapsed);

  useEffect(() => {
    setIsCollapsed(collapsed);
  }, [collapsed]);

  return (
    <View
      style={{
        backgroundColor: theme.colors.card.default.backgroundColor,
        borderWidth: 1,
        borderColor: theme.colors.card.hover.borderColor,
        overflow: 'hidden',
        ...(groupPosition === 'top'
          ? {
              borderTopLeftRadius: 24,
              borderTopRightRadius: 24,
              borderBottomWidth: 0,
            }
          : groupPosition === 'bottom'
          ? {
              borderBottomLeftRadius: 24,
              borderBottomRightRadius: 24,
              borderTopWidth: 0,
            }
          : { borderRadius: 24 }),
      }}
    >
      <Pressable
        onPress={() => {
          setIsCollapsed((prev) => !prev);
          onToggle?.(!isCollapsed);
        }}
        style={{
          flexDirection: 'row',
          alignItems: 'center',
          justifyContent: 'space-between',
          gap: 8,
          padding: 16,
          backgroundColor: theme.colors.card.hover.backgroundColor,
          ...(!isCollapsed
            ? {
                borderBottomWidth: 2,
                borderBottomColor: theme.colors.card.hover.borderColor,
              }
            : null),
        }}
      >
        <Text type="title2">{title}</Text>

        <View style={{ flexDirection: 'row', alignItems: 'center', gap: 8 }}>
          {headerRight}

          <Icon
            type="naked"
            icon={isCollapsed ? <ChevronUp /> : <ChevronDown />}
            fillColor={theme.colors.icon.color}
            width={10}
            height={10}
            style={{ width: 36, height: 36 }}
          />
        </View>
      </Pressable>

      {!isCollapsed ? <View>{children}</View> : null}
    </View>
  );
}
